import React, { useEffect, useRef } from 'react';
import {
  Bold,
  Italic,
  Underline,
  List,
  AlignLeft,
  AlignCenter,
  Type,
  Image as ImageIcon,
  Loader2,
  UploadCloud,
  FileText,
  X,
} from 'lucide-react';
import { cn } from '../lib/utils';

const TemplateStyleNativeRichEditor = ({ value, onChange, onImageUpload, isUploading }) => {
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const execCommand = (command, val = null) => {
    document.execCommand(command, false, val);
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel.getRangeAt && sel.rangeCount) {
      window.savedRange = sel.getRangeAt(0);
    }
  };

  const restoreSelection = () => {
    if (window.savedRange) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(window.savedRange);
    } else if (editorRef.current) {
      editorRef.current.focus();
    }
  };

  const handleImageClick = () => {
    saveSelection();
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        const url = await onImageUpload(file);
        if (url) {
          restoreSelection();
          execCommand('insertImage', url);
        }
      }
    };
    input.click();
  };

  const handleColorChange = (e) => {
    restoreSelection();
    execCommand('foreColor', e.target.value);
  };

  return (
    <div className="flex flex-col h-full bg-background border-2 border-border/50 rounded-3xl overflow-hidden shadow-inner">
      <div className="flex flex-wrap items-center gap-1 p-2 bg-muted/20 border-b border-border shadow-sm">
        <button type="button" onClick={() => execCommand('bold')} className="p-2 hover:bg-primary/10 hover:text-primary rounded-lg transition-colors" title="Bold"><Bold className="w-4 h-4" /></button>
        <button type="button" onClick={() => execCommand('italic')} className="p-2 hover:bg-primary/10 hover:text-primary rounded-lg transition-colors" title="Italic"><Italic className="w-4 h-4" /></button>
        <button type="button" onClick={() => execCommand('underline')} className="p-2 hover:bg-primary/10 hover:text-primary rounded-lg transition-colors" title="Underline"><Underline className="w-4 h-4" /></button>
        <div className="w-px h-4 bg-border mx-1" />
        <button type="button" onClick={() => execCommand('insertUnorderedList')} className="p-2 hover:bg-primary/10 hover:text-primary rounded-lg transition-colors" title="List"><List className="w-4 h-4" /></button>
        <button type="button" onClick={() => execCommand('justifyLeft')} className="p-2 hover:bg-primary/10 hover:text-primary rounded-lg transition-colors" title="Align Left"><AlignLeft className="w-4 h-4" /></button>
        <button type="button" onClick={() => execCommand('justifyCenter')} className="p-2 hover:bg-primary/10 hover:text-primary rounded-lg transition-colors" title="Align Center"><AlignCenter className="w-4 h-4" /></button>
        <div className="w-px h-4 bg-border mx-1" />
        <div className="relative flex items-center group p-2 hover:bg-primary/10 rounded-lg">
          <Type className="w-4 h-4 mr-1 text-muted-foreground" />
          <input type="color" onChange={handleColorChange} className="w-4 h-4 cursor-pointer bg-transparent border-none p-0 overflow-hidden" title="Text Color" />
        </div>
        <button type="button" onClick={handleImageClick} className="p-2 hover:bg-primary/10 hover:text-primary rounded-lg transition-colors" title="Upload Image"><ImageIcon className="w-4 h-4" /></button>
      </div>

      <div className="relative flex-1 flex flex-col min-h-0">
        <div
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          onBlur={saveSelection}
          onKeyUp={saveSelection}
          onMouseUp={saveSelection}
          className="flex-1 p-6 overflow-y-auto focus:outline-none prose prose-zinc max-w-none text-foreground leading-8"
          style={{ minHeight: '300px' }}
        />
        {isUploading && (
          <div className="absolute inset-0 bg-background/40 backdrop-blur-sm flex items-center justify-center z-10">
            <div className="bg-card p-4 rounded-2xl border border-border shadow-xl flex items-center gap-3">
              <Loader2 className="w-5 h-5 text-primary animate-spin" />
              <span className="text-xs font-black uppercase tracking-widest text-primary">Uploading to S3...</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default function TemplateStyleEmailEditorShell({
  title,
  subtitle,
  leadIcon,
  headerActions,
  metaSection,
  subject,
  onSubjectChange,
  subjectPlaceholder,
  variableNames = [],
  onInsertVariable,
  bodyHtml,
  onBodyChange,
  onImageUpload,
  isUploading,
  attachments = [],
  onAttachmentUpload,
  onAttachmentRemove,
  previewData,
  previewLoading,
  showAttachments = true,
  showPreviewPane = true,
  onDiscard,
  onSave,
  discardLabel,
  saveLabel,
  saveIcon,
  saveDisabled = false,
  className,
}) {
  const LeadIcon = leadIcon;
  const SaveIcon = saveIcon || FileText;

  return (
    <div className={cn("flex h-full flex-col overflow-hidden bg-card text-card-foreground", className)}>
      <div className="px-8 py-5 border-b border-border bg-card/50 backdrop-blur-md flex justify-between items-center shrink-0">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-primary text-white flex items-center justify-center rotate-3">
            {LeadIcon ? <LeadIcon className="w-6 h-6" /> : <FileText className="w-6 h-6" />}
          </div>
          <div>
            <h2 className="text-2xl font-black tracking-tight leading-tight">{title}</h2>
            {subtitle ? (
              <p className="text-[10px] text-muted-foreground font-black uppercase tracking-[0.2em] opacity-70">{subtitle}</p>
            ) : null}
          </div>
        </div>
        {headerActions ? <div className="flex items-center gap-3">{headerActions}</div> : null}
      </div>

      <div className="flex-1 flex overflow-hidden bg-background/50 min-h-0">
        <div className={cn(
          "flex flex-col h-full overflow-y-auto border-r border-border transition-all duration-500 p-8 space-y-8 bg-card",
          showPreviewPane ? 'w-full lg:w-1/2' : 'w-full'
        )}>
          {metaSection ? <div className="space-y-6">{metaSection}</div> : null}

          <div className="flex-1 flex flex-col min-h-[400px]">
            <div className="space-y-6 mb-6">
              <div>
                <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">Communication Subject</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => onSubjectChange(e.target.value)}
                  placeholder={subjectPlaceholder}
                  className="w-full bg-muted/30 border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:border-primary sm:text-lg font-black py-4 px-5 transition-all"
                />
              </div>
            </div>

            <div className="flex justify-between items-center mb-4">
              <label className="text-sm font-black flex items-center gap-2 uppercase tracking-tight">
                <FileText className="w-5 h-5 text-primary" /> Rich Text Architect
              </label>
              <div className="flex gap-2 flex-wrap justify-end">
                {variableNames.map((variable) => (
                  <button
                    key={variable}
                    type="button"
                    onClick={() => onInsertVariable(variable)}
                    className="text-[10px] px-3 py-1.5 bg-card border border-border hover:border-primary hover:text-primary rounded-xl font-black transition-all active:scale-90"
                  >
                    +{variable}
                  </button>
                ))}
              </div>
            </div>

            <TemplateStyleNativeRichEditor
              value={bodyHtml}
              onChange={onBodyChange}
              onImageUpload={onImageUpload}
              isUploading={isUploading}
            />
          </div>

          {showAttachments ? (
            <div className="bg-muted/10 border-2 border-border border-dashed rounded-[2rem] p-6 transition-all hover:bg-muted/20">
              <div className="flex items-center justify-between mb-4">
                <label className="text-xs font-black flex items-center gap-2 uppercase tracking-tight text-foreground/80">
                  <UploadCloud className="w-4 h-4 text-primary" /> Attachments
                </label>
                {onAttachmentUpload ? (
                  <div className="relative">
                    <input
                      type="file"
                      onChange={onAttachmentUpload}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      title="Upload File"
                    />
                    <button type="button" className="text-[10px] px-4 py-2 bg-primary/10 text-primary hover:bg-primary hover:text-white rounded-xl font-black transition-all active:scale-90 flex items-center gap-1.5">
                      <UploadCloud className="w-3 h-3" /> Add File
                    </button>
                  </div>
                ) : null}
              </div>

              {attachments.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {attachments.map((att, idx) => (
                    <div key={`${att.filename || 'attachment'}-${idx}`} className="flex items-center justify-between p-3 bg-card border border-border shadow-sm rounded-2xl group">
                      <div className="flex items-center gap-3 overflow-hidden">
                        <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                          <FileText className="w-4 h-4 text-primary" />
                        </div>
                        <div className="truncate">
                          <p className="text-sm font-bold truncate text-foreground">{att.filename}</p>
                          <p className="text-[10px] text-muted-foreground uppercase font-black tracking-widest">{att.size ? `${(att.size / 1024).toFixed(1)} KB` : 'Attached'}</p>
                        </div>
                      </div>
                      {onAttachmentRemove ? (
                        <button
                          type="button"
                          onClick={() => onAttachmentRemove(idx)}
                          className="p-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-xl transition-colors opacity-0 group-hover:opacity-100"
                        >
                          <span className="sr-only">Remove attachment</span>
                          <X className="w-4 h-4" />
                        </button>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <p className="text-[11px] font-bold text-muted-foreground/60 uppercase tracking-widest">No attachments added yet</p>
                </div>
              )}
            </div>
          ) : null}
        </div>

        {showPreviewPane ? (
          <div className="hidden lg:flex w-1/2 flex-col bg-muted/5 relative overflow-hidden transition-all">
            <div className="flex-1 p-10 overflow-y-auto">
              {previewLoading ? (
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground/30 space-y-6">
                  <Loader2 className="w-10 h-10 animate-spin text-primary/50" />
                  <p className="text-lg font-black uppercase tracking-[0.3em] opacity-40">Refreshing Preview</p>
                </div>
              ) : previewData ? (
                <div className="bg-white text-[#1e1e1f] rounded-[2rem] shadow-2xl border border-zinc-200/50 mx-auto max-w-3xl overflow-hidden min-h-[600px] flex flex-col">
                  <div className="border-b border-zinc-100 p-8 bg-zinc-50/70">
                    <h3 className="text-2xl font-bold tracking-tight text-zinc-900">{previewData?.resolvedSubject || "Blueprint Subject"}</h3>
                  </div>
                  <div className="p-10 flex-1 bg-white">
                    <div className="prose prose-zinc prose-sm md:prose-base max-w-none text-zinc-800" dangerouslySetInnerHTML={{ __html: previewData?.resolvedBodyHtml || '<p class="opacity-20 uppercase font-black tracking-widest text-center mt-20">Awaiting architecture...</p>' }} />
                  </div>
                  {previewData?.attachments && previewData?.attachments.length > 0 ? (
                    <div className="bg-zinc-50 border-t border-zinc-100 p-6 flex flex-col gap-2">
                      <h4 className="text-xs font-black uppercase tracking-widest text-zinc-400">Attachments ({previewData?.attachments.length})</h4>
                      <div className="flex flex-wrap gap-2">
                        {previewData?.attachments.map((att, i) => (
                          <div key={`${att.filename || 'preview-attachment'}-${i}`} className="flex items-center gap-2 bg-white border border-zinc-200 px-3 py-2 rounded-xl text-sm shadow-sm max-w-[200px]">
                            <FileText className="w-4 h-4 text-zinc-400 shrink-0" />
                            <span className="truncate text-zinc-700 font-medium">{att.filename}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground/30 space-y-6">
                  <div className="w-24 h-24 rounded-full bg-muted/40 animate-pulse" />
                  <p className="text-lg font-black uppercase tracking-[0.3em] opacity-40">Initializing Engine</p>
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>

      <div className="px-10 py-6 border-t border-border bg-card/80 flex justify-end gap-5 shrink-0">
        <button type="button" onClick={onDiscard} className="px-8 py-3 text-[10px] font-black uppercase tracking-widest text-muted-foreground transition-all">
          {discardLabel}
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saveDisabled}
          className="px-12 py-4 text-[10px] font-black uppercase tracking-widest text-white bg-primary rounded-2xl shadow-xl transition-all active:scale-[0.98] flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <SaveIcon className="w-4 h-4" /> {saveLabel}
        </button>
      </div>
    </div>
  );
}
